<template>
  <div class="preview-container">
    <!-- Header for small screens -->
    <h4 v-if="isSmall" class="preview-header">Náhled oznámení</h4>
    
    <!-- Tools -->
    <div v-if="!isLoading" class="preview-tools">
      <v-slide-group mandatory v-model="previewLanguage">
        <div v-if="selectedLanguages.length > 1">
          <v-slide-group-item
            v-for="language in selectedLanguages"
            :key="language"
            :value="language"
            v-slot="{ isSelected, toggle }"
          >
            <v-btn rounded="xl"
                   variant="flat"
                   size="x-small"
                   :id="`preview-${language}-btn`"
                   class="mr-1 mb-2 "
                   :class = "isSelected ? 'selected-btn' : ''"
                   @click="toggle"
            >
              {{ language }}
            </v-btn>
          </v-slide-group-item>
        </div>
      </v-slide-group>

      <v-slide-group v-if="!blockParallel" mandatory v-model="previewOrientation">
        <v-slide-group-item key="blockParallel" value="blockParallel" v-slot="{ isSelected, toggle }">
          <v-btn
            id="preview-blockParallel-btn"
            variant="flat"
            size="x-small"
            rounded="xl"
            class="mr-1 mb-2 p-0 "
            :class = "isSelected ? 'selected-btn' : ''"
            @click="toggle"
          >
            celá obrazovka
          </v-btn>
        </v-slide-group-item>
        <v-slide-group-item
          key="portrait"
          value="portrait"
          v-slot="{ isSelected, toggle }"
        >
          <v-btn
            id="preview-portrait-btn"
            rounded="xl"
            variant="flat"
            size="x-small"
            class="mr-1 mb-2 p-0 "
            :class = "isSelected ? 'selected-btn' : ''"
            @click="toggle"
          >
            na výšku
          </v-btn>
        </v-slide-group-item>
        <v-slide-group-item
          key="landscape"
          value="landscape"
          v-slot="{ isSelected, toggle }"
        >
          <v-btn
            id="preview-landscape-btn"
            rounded="xl"
            variant="flat"
            size="x-small"
            class="mr-0 mb-2 p-0 "
            :class = "isSelected ? 'selected-btn' : ''"
            @click="toggle"
          >
            na šířku
          </v-btn>
        </v-slide-group-item>
      </v-slide-group>
    </div>
    
    <!-- Preview iframe wrapper -->
    <div id="preview-wrapper" class="preview-wrapper">
      <iframe
        :src="`${config.displaysUrl}preview`"
        :class="`preview preview-${previewOrientation}${(isLargeDevice && inDialog) ? '-lg' : (isSmall ? '-sm' : '')}`"
        :id="`preview-frame${inDialog ? '-dialog' : ''}`"
        @load="() => {
          onUpdateContent();
          isLoading = false;
        }"
        scrolling="no"
      ></iframe>

      <v-btn
        v-if="!inDialog && !isLoading"
        id="zoom-preview-btn"
        class="zoom-icon mb-3"
        color="white"
        @click="$emit('zoomIn')"
        size="x-large"
        icon="zoom_in"
        variant="tonal"
        density="comfortable"
      >
      </v-btn>

      <Loading v-if="isLoading" class="iframe-loader"/>
    </div>
  </div>
</template>

<script setup>
import {computed, ref, toRaw, watch} from "vue";
import {config, displaysMaxValues} from '@/config';
import Loading from "@/components/Loading.vue";
import {useDisplay} from "vuetify";

const { name } = useDisplay()

const props = defineProps({
  announcement: Object,
  selectedAnnouncementType: Object,
  selectedLanguages: Array,
  textLength: Function,
  getShortTitle: Function,
  getShortText: Function,
  announcementImagePath: String,
  announcementImageIsVideo: Boolean,
  blockParallel: Boolean,
  inDialog: Boolean,
  isSmall: Boolean,
  initialLanguage: String,
  initialOrientation: String,
})
const emit = defineEmits(['orientationChanged','languageChanged'])

const previewLanguage = ref(props.initialLanguage)
const previewOrientation = ref(props.initialOrientation)
const isLoading = ref(true)

const previewText = computed(()=>{
  return props.announcement.localizedAttributes[previewLanguage.value || props.selectedLanguages[0]].text;
})
const previewShortText = computed(()=>{
  const language = previewLanguage.value || props.selectedLanguages[0];
  return props.textLength(language) > displaysMaxValues.announcement.shortText
      ? props.announcement.localizedAttributes[language].shortText
      : undefined;
})
const selectedAnnouncementTypeServiceName = computed(()=>{
  return props.selectedAnnouncementType ? props.selectedAnnouncementType.serviceName : null;
})

const isLargeDevice = computed(()=>{
  return !['xs', 'sm', 'md'].includes(name.value);
})

function onUpdateContent() {
  const iframe = document.getElementById(`preview-frame${props.inDialog ? '-dialog' : ''}`);
  const _previewLanguage = previewLanguage.value || props.selectedLanguages[0];

  iframe?.contentWindow.postMessage({
    announcement: {
      id: props.announcement.id,
      blockParallel: props.announcement.blockParallel,
      color: props.selectedAnnouncementType.color,
      iconName: props.selectedAnnouncementType.iconName,
      language: _previewLanguage,
      announcementImagePath: props.announcementImagePath,
      announcementImageIsVideo: props.announcementImageIsVideo,
      title: props.announcement.localizedAttributes[_previewLanguage]?.title,
      shortTitle: props.getShortTitle(_previewLanguage),
      text: props.announcement.localizedAttributes[_previewLanguage]?.text,
      shortText: props.getShortText(_previewLanguage),
      url: props.announcement.localizedAttributes[_previewLanguage]?.url,
      previewOrientation: previewOrientation.value,
      serviceConfiguration: toRaw(props.announcement.serviceConfiguration) || {},
      serviceName: selectedAnnouncementTypeServiceName.value
    }
  }, config.displaysUrl);

}

function updateContentAndEmit(event, value) {
  onUpdateContent();
  emit(event, value);
}

function setPreviewLanguage(language) {
  previewLanguage.value = language;
  onUpdateContent();
}

function setPreviewOrientation(orientation) {
  previewOrientation.value = orientation;
  onUpdateContent();
}

watch(previewLanguage, ()=>{
  updateContentAndEmit('languageChanged', previewLanguage.value)
})
watch(previewOrientation, ()=>{
  updateContentAndEmit('orientationChanged', previewOrientation.value)
})
watch(previewText, ()=>{
  onUpdateContent()
})
watch(previewShortText, ()=>{
  onUpdateContent();
})
watch(selectedAnnouncementTypeServiceName, ()=>{
  onUpdateContent();
})
watch(() => props.selectedLanguages, (newLanguages)=>{
  if (!newLanguages.includes(previewLanguage.value)) {
    previewLanguage.value = newLanguages[0];
  }
})
watch(() => props.announcementImagePath, ()=>{
  onUpdateContent();
})
watch(() => props.blockParallel, (newValue)=>{
  previewOrientation.value = newValue ? 'blockParallel' : previewOrientation.value;
})
watch(() => props.initialOrientation, (newValue)=>{
  previewOrientation.value = newValue;
})

defineExpose({
  setPreviewLanguage,
  setPreviewOrientation,
  onUpdateContent
})
</script>

<style scoped>
  .preview-wrapper {
    overflow: hidden;
    position: relative;
  }

  .preview {
    border: none;
  }

  .preview-landscape-sm {
    width: 760px;
    height: calc(0.93 * 427.5px);
    margin-left: calc(-0.5 * 760px);
    margin-top: calc(-0.5 * 0.935 * 427.5px);
    margin-bottom: 0.1vw;
  }

  .preview-landscape {
    width: max(75vw, 700px);
    height: calc(0.93 * max(42.2vw, 393.75px));
    margin-left: calc(-0.5 * max(75vw, 700px));
    margin-top: calc(-0.5 * 0.935 * max(42.2vw, 393.75px));
    margin-bottom: 0.1vw;
  }

  .preview-landscape-lg {
    width: max(75vw, 1400px);
    height: calc(0.93 * max(42.2vw, 787.5px));
    margin-left: calc(-0.5 * max(75vw, 1400px));
    margin-top: calc(-0.5 * 0.935 * max(42.2vw, 787.5px));
    margin-bottom: 0.1vw;
  }

  .preview-blockParallel-sm {
    width: 380px;
    height: calc(0.93 * 213.75px);
  }
  
  .preview-blockParallel {
    width: max(37.5vw, 350px);
    height: calc(0.93 * max(21.1vw, 196.875px));
  }

  .preview-blockParallel-lg {
    width: max(75vw, 700px);
    height: calc(0.93 * max(42.1875vw, 393.75px));
  }

  .preview-portrait-sm {
    width: 760px;
    height: calc(0.93 * 427.5px);
    margin-right: calc(-0.5 * 760px);
  }
  
  .preview-portrait {
    width: 720px;
    height: calc(0.93 * 405px);
    margin-right: calc(-0.5 * 720px);
  }

  .preview-portrait-lg {
    width: 1440px;
    height: calc(0.93 * 810px);
    margin-right: calc(-0.5 * 1440px);
  }

  .preview-tools {
    margin-top: 12px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  .preview-container {
    width: fit-content;
  }

  .zoom-icon {
    background: rgba(100,100,100,0.5);
  }

  #preview-wrapper > button {
    position: absolute;
    float: left;
    left: 30px;
    bottom: 25px;
  }

  #preview-wrapper > .iframe-loader {
    position: absolute;
    float: left;
    left: calc(50% - 35px);
    bottom: calc(50% - 45px);
  }

  .preview-header {
    font-size: .75rem
  }

  .selected-btn {
    color: white !important;
    background-color: #78BE13 !important;
  }
</style>